import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/auth.service';
import { sessionService } from '../services/session.service';
import profileService, { UserProfile as ProfileData } from '../services/profile.service';
import { verificationService, VerificationStatus } from '../services/verification.service';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider: 'google' | 'linkedin' | 'email';
  resume?: {
    url: string;
    path?: string;
    uploadedAt: string;
  };
  salaryRange?: {
    min: number;
    max: number;
  };
  skills?: string[];
  companyId?: string;
  companyName?: string;
  // Extended profile data from API
  profileData?: ProfileData;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
  verificationStatus: VerificationStatus | null;
  login: (provider: 'google' | 'linkedin') => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshVerificationStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);

  useEffect(() => {
    // Initialize session from cookies
    const session = sessionService.initializeSession();
    const token = localStorage.getItem('jobometry_auth_token');
    
    if (session || token) {
      // Session or token exists, check if still valid
      checkAuth();
    } else {
      setLoading(false);
    }

    // Listen for OAuth login events
    const handleAuthLogin = async (event: CustomEvent<UserProfile>) => {
      setUser(event.detail);
      setIsAuthenticated(true);
      setLoading(false);

      // Create session with the new auth data
      const token = localStorage.getItem('jobometry_auth_token');
      if (token) {
        sessionService.createSession(token, {
          userId: event.detail.id,
          email: event.detail.email,
          name: event.detail.name
        });
      }

      // Fetch verification status for the new user
      try {
        const status = await verificationService.getVerificationStatus();
        setVerificationStatus(status);
      } catch {
        // Expected for new unverified users
      }
    };

    window.addEventListener('auth:login', handleAuthLogin as unknown as EventListener);

    return () => {
      window.removeEventListener('auth:login', handleAuthLogin as unknown as EventListener);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await authService.getMe();
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
        
        // Try to fetch full profile data
        try {
          const profileData = await profileService.getProfile();
          setUser(prev => prev ? { ...prev, profileData } : null);
        } catch (profileError) {
          // Profile fetch failed, but auth is still valid
          console.log('Could not fetch full profile data');
        }
        
        // Check verification status
        try {
          const status = await verificationService.getVerificationStatus();
          setVerificationStatus(status);
        } catch (verificationError) {
          console.log('Could not fetch verification status');
        }
      }
    } catch (error) {
      // User not authenticated
      console.log('User not authenticated');
    } finally {
      setLoading(false);
    }
  };
  
  const refreshVerificationStatus = async () => {
    try {
      const status = await verificationService.getVerificationStatus();
      setVerificationStatus(status);
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    }
  };

  const login = async (provider: 'google' | 'linkedin') => {
    try {
      setLoading(true);
      await authService.login(provider);
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      await authService.loginWithEmail(email, password);
    } catch (error) {
      console.error('Email login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const registerWithEmail = async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      await authService.registerWithEmail(email, password, name);
    } catch (error) {
      console.error('Email registration failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      sessionService.clearSession(); // Clear session cookie
      verificationService.clearVerificationStatus(); // Clear verification cache
      setUser(null);
      setIsAuthenticated(false);
      setVerificationStatus(null);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      // Update via auth service for backward compatibility
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
      
      // Also try to fetch updated profile data
      try {
        const profileData = await profileService.getProfile();
        setUser(prev => prev ? { ...prev, profileData } : null);
      } catch (profileError) {
        console.log('Could not fetch updated profile data');
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        verificationStatus,
        login,
        loginWithEmail,
        registerWithEmail,
        logout,
        updateProfile,
        refreshVerificationStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};